import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllFeedback,
  fetchFeedbackStats,
  updateFeedbackStatus,
  deleteFeedback,
  selectAdminFeedback,
  selectFeedbackStats,
  selectFeedbackLoading,
  selectFeedbackError,
  selectFeedbackPagination,
} from "../../store/slices/adminFeedbackSlice";
import {
  createChitalishte,
  updateChitalishte,
  fetchAllChitalishta,
  selectAllChitalishta,
} from "../../store/slices/chitalishteAdminSlice";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
  Hidden,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fab,
  Avatar,
  Snackbar,
} from "@mui/material";
import {
  Refresh,
  Visibility,
  Edit,
  Delete,
  CheckCircle,
  HourglassEmpty,
  Block,
  Warning,
  Email,
  Message,
  Person,
  FilterList,
  MoreVert,
  Menu as MenuIcon,
  Menu,
  ArrowBack,
  ArrowForward,
  Close,
} from "@mui/icons-material";
import FeedbackDetailModal from "./FeedbackDetailModal";
import FeedbackExportButton from "./feedback/FeedbackExportButton";
import ChitalishteForm from "./chitalishte/ChitalishteForm";

const FeedbackDashboard = ({ requestDbAction }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const feedback = useSelector(selectAdminFeedback);
  const stats = useSelector(selectFeedbackStats);
  const loading = useSelector(selectFeedbackLoading);
  const pagination = useSelector(selectFeedbackPagination);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    isMobile ? 5 : isTablet ? 10 : 20,
  );
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState(isMobile ? "cards" : "table");
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  const [chitalishteFormOpen, setChitalishteFormOpen] = useState(false);
  const [selectedChitalishteId, setSelectedChitalishteId] = useState(null);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    loadData();
    dispatch(fetchAllChitalishta({ page: 0, size: 5000 }));
  }, [page, rowsPerPage, statusFilter]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const loadData = () => {
    dispatch(
      fetchAllFeedback({
        page,
        size: rowsPerPage,
        status: statusFilter,
      }),
    );
    dispatch(fetchFeedbackStats());
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setPage(0);
  };

  const handleViewDetails = (feedback) => {
    setSelectedFeedback(feedback);
    setDetailModalOpen(true);
  };

  const handleOpenChitalishteForm = (chitalishteId, feedbackId) => {
    setSelectedChitalishteId(chitalishteId);
    setSelectedFeedbackId(feedbackId);
    setChitalishteFormOpen(true);
  };

  const handleChitalishteSubmit = async (formData) => {
    try {
      const { shouldUpdateFeedback, feedbackId, ...chitalishteData } = formData;
      const feedbackIdToSend =
        feedbackId && feedbackId.trim() ? feedbackId.trim() : null;

      if (selectedChitalishteId) {
        await dispatch(
          updateChitalishte({
            id: selectedChitalishteId,
            data: chitalishteData,
            feedbackId: feedbackIdToSend,
          }),
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Читалището е обновено успешно!",
          severity: "success",
        });
      } else {
        await dispatch(
          createChitalishte({
            data: chitalishteData,
            feedbackId: feedbackIdToSend,
          }),
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Читалището е създадено успешно!",
          severity: "success",
        });
      }

      if (shouldUpdateFeedback && feedbackIdToSend) {
        await dispatch(
          updateFeedbackStatus({
            id: feedbackIdToSend,
            status: "PROCESSED",
            processedBy: "admin",
            notes: "Автоматично обработен при редакция на читалището",
          }),
        ).unwrap();
      }

      setChitalishteFormOpen(false);
      setSelectedChitalishteId(null);
      setSelectedFeedbackId(null);
      loadData(); // Refresh feedback list
      dispatch(fetchAllChitalishta({ page: 0, size: 5000 })); // Refresh chitalishta list
    } catch (error) {
      console.error("Error in handleChitalishteSubmit:", error);
      setSnackbar({
        open: true,
        message: "Грешка при запис на читалището.",
        severity: "error",
      });
    }
  };

  const handleEditLibrary = (chitalishte, feedbackId) => {
    setChitalishteFormOpen(false);
    requestDbAction({
      type: 'editLibrary',
      chitalishte: chitalishte,
      feedbackId: feedbackId,
    });
  };

  const handleDeleteClick = (feedback) => {
    setFeedbackToDelete(feedback);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (feedbackToDelete) {
      dispatch(deleteFeedback(feedbackToDelete.id));
      setDeleteDialogOpen(false);
      setFeedbackToDelete(null);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      NEW: { color: "warning", icon: <Warning />, label: "Нов" },
      IN_REVIEW: {
        color: "info",
        icon: <HourglassEmpty />,
        label: "В преглед",
      },
      PROCESSED: {
        color: "success",
        icon: <CheckCircle />,
        label: "Обработен",
      },
      REJECTED: { color: "error", icon: <Block />, label: "Отхвърлен" },
    };

    const config = statusConfig[status] || statusConfig.NEW;

    return (
      <Chip
        icon={config.icon}
        label={isMobile ? "" : config.label}
        color={config.color}
        size="small"
        variant="outlined"
        sx={{
          minWidth: isMobile ? 24 : "auto",
          "& .MuiChip-label": {
            display: isMobile ? "none" : "inline-flex",
          },
        }}
      />
    );
  };

  const getFeedbackTypeLabel = (type) => {
    const typeLabels = {
      location: "Местоположение",
      basic_info: "Основни данни",
      activity: "Дейност",
      website: "Уебсайт",
      other: "Друго",
    };
    return typeLabels[type] || type;
  };

  const truncateText = (text, length) => {
    if (!text) return "";
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  const renderMobileCard = (item) => (
    <Card key={item.id} sx={{ mb: 2, position: "relative" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ flex: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
                <Person fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight="600">
                  {item.senderName || "Анонимен"}
                </Typography>
                {item.email && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Email sx={{ fontSize: 10, mr: 0.5 }} />
                    {truncateText(item.email, 20)}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Тип:</strong> {getFeedbackTypeLabel(item.feedbackType)}
            </Typography>

            {item.chitalishteName && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Читалище:</strong>{" "}
                {truncateText(item.chitalishteName, 25)}
              </Typography>
            )}

            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Съобщение:</strong> {truncateText(item.message, 60)}
            </Typography>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(item.createdAt).toLocaleDateString("bg-BG")}
                </Typography>
              </Box>
              {getStatusChip(item.status)}
            </Stack>
          </Box>

          <IconButton size="small" onClick={() => setDrawerOpen(true)}>
            <MoreVert />
          </IconButton>
        </Stack>
      </CardContent>

      {/* Mobile Actions Drawer */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Действия</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
          <List>
            <ListItem
              button
              onClick={() => {
                handleViewDetails(item);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>
                <Visibility />
              </ListItemIcon>
              <ListItemText primary="Преглед на детайли" />
            </ListItem>
            {item.chitalishteId && (
              <ListItem
                button
                onClick={() => {
                  handleOpenChitalishteForm(item.chitalishteId, item.id);
                  setDrawerOpen(false);
                }}
              >
                <ListItemIcon>
                  <Edit />
                </ListItemIcon>
                <ListItemText primary="Редактирай читалище" />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </Card>
  );

  const renderTabletCard = (item) => (
    <Card key={item.id} sx={{ mb: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                {item.senderName || "Анонимен"}
              </Typography>
              {item.email && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Email sx={{ fontSize: 12, mr: 0.5 }} />
                  {item.email}
                </Typography>
              )}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                {getStatusChip(item.status)}
                <Chip
                  label={getFeedbackTypeLabel(item.feedbackType)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Читалище:</strong> {item.chitalishteName || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Съобщение:</strong> {truncateText(item.message, 80)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Stack direction="column" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {new Date(item.createdAt).toLocaleDateString("bg-BG")}
                <br />
                {new Date(item.createdAt).toLocaleTimeString("bg-BG")}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Преглед">
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(item)}
                    color="primary"
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                {item.chitalishteId && (
                  <Tooltip title="Редактирай читалище">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleOpenChitalishteForm(item.chitalishteId, item.id)
                      }
                      color="secondary"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {/* <Tooltip title="Изтрий">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(item)}
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip> */}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading && feedback.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Header with stats */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="700">
              Управление на обратна връзка
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Общо: {pagination.totalElements || 0} сигнала
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            {!isMobile && (
              <Button
                startIcon={<FilterList />}
                onClick={() =>
                  setViewMode(viewMode === "table" ? "cards" : "table")
                }
                variant="outlined"
                size="small"
              >
                {viewMode === "table" ? "Картички" : "Таблица"}
              </Button>
            )}

            {/* Добавяме бутона за експорт ПРЕДИ Refresh бутона */}
            <FeedbackExportButton variant="contained" size="small" />

            <Button
              startIcon={<Refresh />}
              onClick={handleRefresh}
              variant="contained"
              size="small"
              sx={{
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "white",
              }}
            >
              {isMobile ? "" : "Опресни"}
            </Button>
          </Stack>
        </Stack>

        {/* Stats cards - responsive grid */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              {
                key: "new",
                label: "Нови",
                icon: Warning,
                color: "#fff8e1",
                iconColor: "#ff9800",
              },
              {
                key: "inReview",
                label: "В преглед",
                icon: HourglassEmpty,
                color: "#e3f2fd",
                iconColor: "#2196f3",
              },
              {
                key: "processed",
                label: "Обработени",
                icon: CheckCircle,
                color: "#e8f5e9",
                iconColor: "#4caf50",
              },
              {
                key: "rejected",
                label: "Отхвърлени",
                icon: Block,
                color: "#ffebee",
                iconColor: "#f44336",
              },
            ].map((stat) => (
              <Grid item xs={6} sm={3} key={stat.key}>
                <Card
                  sx={{
                    bgcolor: stat.color,
                    height: "100%",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ height: "100%" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: { xs: 36, sm: 44 },
                          height: { xs: 36, sm: 44 },
                          borderRadius: "50%",
                          bgcolor: "white",
                          p: 1,
                        }}
                      >
                        <stat.icon
                          sx={{
                            color: stat.iconColor,
                            fontSize: { xs: 20, sm: 24 },
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant={isMobile ? "h5" : "h4"}
                          fontWeight="700"
                          sx={{ lineHeight: 1 }}
                        >
                          {stats[stat.key] || 0}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Filter UI */}
        {isMobile ? (
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<FilterList />}
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
              variant="outlined"
              fullWidth
            >
              Филтри {statusFilter && `(${statusFilter})`}
            </Button>
            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={() => setFilterMenuAnchor(null)}
              PaperProps={{
                style: {
                  width: '100%',
                  maxWidth: 320,
                },
              }}
            >
              {[
                { value: "", label: "Всички" },
                { value: "NEW", label: "Нови" },
                { value: "IN_REVIEW", label: "В преглед" },
                { value: "PROCESSED", label: "Обработени" },
                { value: "REJECTED", label: "Отхвърлени" },
              ].map((filter) => (
                <MenuItem
                  key={filter.value}
                  selected={filter.value === statusFilter}
                  onClick={() => {
                    handleStatusFilterChange(filter.value);
                    setFilterMenuAnchor(null);
                  }}
                >
                  {filter.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : (
          <Box
            sx={{
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: 2 },
              "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: 2 },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 2, width: "max-content", minWidth: "100%" }}
            >
              {[
                { value: "", label: "Всички", color: "primary" },
                { value: "NEW", label: "Нови", color: "warning" },
                { value: "IN_REVIEW", label: "В преглед", color: "info" },
                { value: "PROCESSED", label: "Обработени", color: "success" },
                { value: "REJECTED", label: "Отхвърлени", color: "error" },
              ].map((filter) => (
                <Chip
                  key={filter.value}
                  label={filter.label}
                  onClick={() => handleStatusFilterChange(filter.value)}
                  color={statusFilter === filter.value ? filter.color : "default"}
                  variant={statusFilter === filter.value ? "filled" : "outlined"}
                  size="medium"
                  sx={{ minWidth: 90, justifyContent: "center" }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Content area - conditional rendering based on view mode and screen size */}
      {viewMode === "table" && !isMobile ? (
        // Desktop table view
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            display: { xs: "none", sm: "block" },
          }}
        >
          <TableContainer sx={{ maxHeight: isTablet ? 500 : 600 }}>
            <Table stickyHeader size={isTablet ? "small" : "medium"}>
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ width: { xs: 80, md: 100 } }}>ID</TableCell>
                  <TableCell>Имена / Имейл</TableCell>
                  <Hidden mdDown>
                    <TableCell>Тип</TableCell>
                    <TableCell>Читалище</TableCell>
                  </Hidden>
                  <TableCell sx={{ width: { xs: 150, md: 200 } }}>
                    Съобщение
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>Статус</TableCell>
                  <Hidden smDown>
                    <TableCell sx={{ width: 120 }}>Дата</TableCell>
                  </Hidden>
                  <TableCell sx={{ width: 100 }}>Действие</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedback.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <TableCell>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {item.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="500"
                          noWrap
                          sx={{ maxWidth: 150 }}
                        >
                          {item.senderName || "Анонимен"}
                        </Typography>
                        {item.email && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 150,
                            }}
                          >
                            <Email
                              sx={{ fontSize: 10, mr: 0.5, flexShrink: 0 }}
                            />
                            {item.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <Hidden mdDown>
                      <TableCell>
                        <Chip
                          label={getFeedbackTypeLabel(item.feedbackType)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 150 }}
                        >
                          {item.chitalishteName || "-"}
                        </Typography>
                      </TableCell>
                    </Hidden>
                    <TableCell>
                      <Tooltip title={item.message}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: { xs: 120, md: 180 },
                            cursor: "help",
                          }}
                        >
                          {truncateText(item.message, isTablet ? 30 : 40)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                    <Hidden smDown>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {new Date(item.createdAt).toLocaleDateString("bg-BG")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.createdAt).toLocaleTimeString(
                            "bg-BG",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </Typography>
                      </TableCell>
                    </Hidden>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Преглед">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(item)}
                            color="primary"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {item.chitalishteId && (
                          <Tooltip title="Редактирай читалище">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenChitalishteForm(
                                  item.chitalishteId,
                                  item.id,
                                )
                              }
                              color="secondary"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={pagination.totalElements || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Редове:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} от ${count}`
            }
            rowsPerPageOptions={[5, 10, 20, 50]}
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                },
            }}
          />
        </Paper>
      ) : (
        // Mobile/Tablet card view
        <Box>
          {feedback.map((item) =>
            isMobile ? renderMobileCard(item) : renderTabletCard(item),
          )}

          {/* Mobile pagination */}
          {isMobile && feedback.length > 0 && (
            <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  Страница {page + 1} от{" "}
                  {Math.ceil((pagination.totalElements || 0) / rowsPerPage)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    size="small"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    <ArrowBack />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setPage(page + 1)}
                    disabled={
                      page >=
                      Math.ceil((pagination.totalElements || 0) / rowsPerPage) -
                        1
                    }
                  >
                    <ArrowForward />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* Tablet pagination */}
          {isTablet && !isMobile && (
            <TablePagination
              component="div"
              count={pagination.totalElements || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Редове:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} от ${count}`
              }
              rowsPerPageOptions={[5, 10, 20]}
              sx={{
                mt: 2,
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "center",
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Empty state */}
      {!loading && feedback.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Message
              sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5 }}
            />
          </Box>
          <Typography variant="h6" gutterBottom>
            Няма намерени сигнали
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {statusFilter
              ? `Няма сигнали със статус "${statusFilter}"`
              : "Все още няма получена обратна връзка"}
          </Typography>
          {statusFilter && (
            <Button
              variant="outlined"
              onClick={() => handleStatusFilterChange("")}
              startIcon={<FilterList />}
            >
              Премахни филтър
            </Button>
          )}
        </Paper>
      )}

      {/* Floating Action Button for mobile */}
      {isMobile && (
        <Fab
          color="primary"
          sx={{
            position: "fixed",
            bottom: 80,
            right: 16,
          }}
          onClick={handleRefresh}
        >
          <Refresh />
        </Fab>
      )}

      {/* Modals */}
      {selectedFeedback && (
        <FeedbackDetailModal
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
          }}
          feedback={selectedFeedback}
          onStatusUpdate={async (id, status, notes) => {
            await dispatch(
              updateFeedbackStatus({
                id,
                status,
                processedBy: "admin",
                notes,
              }),
            ).unwrap();

            // НЕ правите refresh тук, по-долу в onSuccess
          }}
          onSuccess={() => {
            // Refresh всички данни след успешна промяна
            dispatch(
              fetchAllFeedback({
                page,
                size: rowsPerPage,
                status: statusFilter,
              }),
            );
            dispatch(fetchFeedbackStats());
          }}
          chitalishte={null}
        />
      )}

      <ChitalishteForm
        open={chitalishteFormOpen}
        onClose={() => {
          setChitalishteFormOpen(false);
          setSelectedChitalishteId(null);
          setSelectedFeedbackId(null);
        }}
        chitalishteId={selectedChitalishteId}
        onSubmit={handleChitalishteSubmit}
        feedbackId={selectedFeedbackId}
        onEditLibrary={handleEditLibrary}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Потвърждение за изтриване</Typography>
            {isMobile && (
              <IconButton onClick={() => setDeleteDialogOpen(false)}>
                <Close />
              </IconButton>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Сигурни ли сте, че искате да изтриете тази обратна връзка?
          </Typography>
          {feedbackToDelete && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "background.default",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" gutterBottom>
                <strong>Име:</strong>{" "}
                {feedbackToDelete.senderName || "Анонимен"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Email:</strong> {feedbackToDelete.email || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Съобщение:</strong>{" "}
                {truncateText(feedbackToDelete.message, 80)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            fullWidth={isMobile}
          >
            Откажи
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            fullWidth={isMobile}
            sx={{ ml: isMobile ? 0 : 1 }}
          >
            Изтрий
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedbackDashboard;
